
import { useEffect, useRef, useState } from "react";
import { useMatch } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { supabase } from "@/integrations/supabase/client";
import LoadingSpinner from "./LoadingSpinner";
import { useUserLocations } from "@/hooks/useUserLocations";
import {
  initializeMap,
  fitMapBounds,
  createMarkerElement,
  createPopupContent,
  createUserLocationMarkerElement,
  createUserLocationPopupContent,
  resolveMarkerCategory,
} from "@/utils/mapUtils";
import type { ResolvedExploreCenter } from "@/utils/locationDefaults";

interface MapProps {
  activeCategory?: string | null;
  initialEquipment?: Array<{
    id: string;
    name: string;
    category: string;
    price_per_day: number;
    currency_code?: string;
    location: { lat: number; lng: number };
    ownerId: string;
    ownerName: string;
  }>;
  userRole?: string;
  searchQuery?: string;
  isEquipmentLoading?: boolean;
  interactive?: boolean;
  ownerIds?: string[];
  viewMode?: 'map' | 'list' | 'hybrid';
  filteredUserLocations?: Array<{
    id: string;
    name: string;
    role: string;
    address: string;
    location: { lat: number; lng: number };
    equipment_categories: string[];
  }>;
  focusedEquipmentId?: string | null;
  onEquipmentSelect?: (equipmentId: string) => void;
  showEquipmentPopups?: boolean;
  initialCenter?: ResolvedExploreCenter | null;
}

const MapComponent = ({
  activeCategory,
  initialEquipment,
  userRole,
  searchQuery: _searchQuery,
  isEquipmentLoading = false,
  interactive = true,
  ownerIds,
  viewMode,
  filteredUserLocations,
  focusedEquipmentId,
  onEquipmentSelect,
  showEquipmentPopups = false,
  initialCenter,
}: MapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);

  // Determine which route we're on
  const isSearchRoute = !!useMatch("/search");
  const isExploreRoute = !!useMatch("/explore");

  // Determine display mode
  const isEquipmentDetailMode = !isSearchRoute && !isExploreRoute && initialEquipment && initialEquipment.length > 0;

  // Only fetch user locations when on explore route
  const {
    data: userLocations = [],
    isLoading: isUserLocationsLoading
  } = useUserLocations();

  // Fetch Mapbox token
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) throw error;
        setMapboxToken(data.token);
      } catch (err) {
        console.error('Error fetching Mapbox token:', err);
        setError('Failed to load map configuration');
      }
    };

    fetchMapboxToken();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) return;

    try {
      const initOptions = initialCenter
        ? { center: [initialCenter.lng, initialCenter.lat] as [number, number], zoom: initialCenter.zoom }
        : undefined;
      map.current = initializeMap(mapContainer.current, mapboxToken, initOptions);
      map.current.on('load', () => {
        setIsLoading(false);
      });
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map');
      setIsLoading(false);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // initialCenter is intentionally read once on map init; later updates come
    // through the focusedEquipmentId flyTo path, not by re-creating the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxToken]);

  // Update markers based on route and data
  useEffect(() => {
    if (!map.current || isLoading) return;

    // Clear existing markers
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    existingMarkers.forEach(marker => marker.remove());

    const showCategoryColors =
      isSearchRoute ||
      viewMode === 'hybrid' ||
      (isExploreRoute && activeCategory !== null);

    if (isEquipmentDetailMode) {
      // Equipment detail mode: Show gear markers and optionally allow popup selection.

      const validEquipment = initialEquipment.filter(item =>
        item.location &&
        typeof item.location.lat === 'number' &&
        typeof item.location.lng === 'number'
      );

      validEquipment.forEach((item) => {
        const el = userRole
          ? createUserLocationMarkerElement(userRole, activeCategory || undefined)
          : createMarkerElement(item.category);

        if (onEquipmentSelect) {
          el.addEventListener('click', () => {
            onEquipmentSelect(item.id);
          });
        }

        const marker = new mapboxgl.Marker(el)
          .setLngLat([item.location.lng, item.location.lat]);

        if (showEquipmentPopups) {
          marker.setPopup(
            new mapboxgl.Popup({ offset: 25 }).setDOMContent(
              createPopupContent(item)
            )
          );
        }

        marker.addTo(map.current!);
      });

      if (validEquipment.length > 0 && !focusedEquipmentId) {
        fitMapBounds(map.current, validEquipment, validEquipment.length === 1);
      }
    } else if (isSearchRoute) {
      // Search route: Show user location markers based on filtered results
      const locationsToShow = filteredUserLocations || userLocations.filter(user =>
        (!ownerIds || ownerIds.includes(user.id)) &&
        (!activeCategory || user.equipment_categories.includes(activeCategory))
      );

      locationsToShow.forEach((user) => {
        if (!user.location?.lat || !user.location?.lng) return;

        const categoryForMarker = showCategoryColors
          ? resolveMarkerCategory(activeCategory, user.equipment_categories)
          : undefined;
        const el = createUserLocationMarkerElement(user.role, categoryForMarker);

        new mapboxgl.Marker(el)
          .setLngLat([user.location.lng, user.location.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setDOMContent(
              createUserLocationPopupContent(user)
            )
          )
          .addTo(map.current!);
      });

      if (locationsToShow.length > 0) {
        fitMapBounds(map.current, locationsToShow);
      }
    } else if (isExploreRoute) {
      // Explore route: Show user location markers

      // Prefer pre-filtered user locations when provided so map markers match list/hybrid results.
      const locationsToShow = filteredUserLocations ?? (
        activeCategory
          ? userLocations.filter(user => user.equipment_categories.includes(activeCategory))
          : userLocations
      );

      locationsToShow.forEach((user) => {
        if (!user.location?.lat || !user.location?.lng) return;

        const categoryForMarker = showCategoryColors
          ? resolveMarkerCategory(activeCategory, user.equipment_categories)
          : undefined;
        const el = createUserLocationMarkerElement(user.role, categoryForMarker);

        new mapboxgl.Marker(el)
          .setLngLat([user.location.lng, user.location.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setDOMContent(
              createUserLocationPopupContent(user)
            )
          )
          .addTo(map.current!);
      });

      // When a resolved center was supplied, keep it as the initial view
      // instead of refitting to the union of all shop pins.
      if (locationsToShow.length > 0 && !initialCenter) {
        fitMapBounds(map.current, locationsToShow);
      }
    }
  }, [isSearchRoute, isExploreRoute, isEquipmentDetailMode, initialEquipment, userLocations, activeCategory, isLoading, ownerIds, userRole, viewMode, filteredUserLocations, focusedEquipmentId, onEquipmentSelect, showEquipmentPopups, initialCenter]);

  useEffect(() => {
    if (!map.current || isLoading || !focusedEquipmentId || !initialEquipment) return;

    const focusedEquipment = initialEquipment.find(
      (item) => item.id === focusedEquipmentId,
    );

    if (!focusedEquipment) return;

    map.current.flyTo({
      center: [focusedEquipment.location.lng, focusedEquipment.location.lat],
      zoom: 15,
      duration: 1000,
    });
  }, [focusedEquipmentId, initialEquipment, isLoading]);

  const showLoading = isLoading ||
    (isSearchRoute ? isEquipmentLoading : (isExploreRoute ? isUserLocationsLoading : false));

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading map</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={mapContainer}
        className={`w-full h-full${!interactive ? ' pointer-events-none' : ''}`}
      />
      {showLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default MapComponent;
